import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Guitar,
  LayoutDashboard,
  Music,
  BookOpen,
  Headphones,
  Video,
  User,
  LogOut,
  Menu,
  X,
  Zap,
  BarChart3,
  MessageCircle,
  MessageSquare,
  Award,
  Home,
} from 'lucide-react';
import { useState } from 'react';
import { useChat } from '../context/ChatContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const chatContext = useChat();
  const unreadCount = chatContext?.unreadCount || 0;
  const openDrawer = chatContext?.openDrawer;

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" /> },
    { path: '/chords', label: 'Chords', icon: <Music className="w-4 h-4" /> },
    { path: '/lessons', label: 'Lessons', icon: <BookOpen className="w-4 h-4" /> },
    { path: '/practice', label: 'Practice', icon: <Headphones className="w-4 h-4" /> },
    { path: '/licks', label: 'Licks', icon: <Zap className="w-4 h-4" /> },
    { path: '/recording', label: 'Record', icon: <Video className="w-4 h-4" /> },
    { path: '/badges', label: 'Badges', icon: <Award className="w-4 h-4" /> },
    { path: '/forums', label: 'Forums', icon: <MessageSquare className="w-4 h-4" /> },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="nav-logo">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img src="/images/string-sauce-logo.png" alt="String Sauce" className="w-full h-full object-cover" />
            </div>
            <span className="text-xl font-bold hidden sm:block">String Sauce</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home Button - Always visible */}
            <Link to="/dashboard" data-testid="nav-home">
              <Button
                variant="ghost"
                className={`px-3 py-2 ${
                  location.pathname === '/dashboard'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10'
                }`}
              >
                <Home className="w-5 h-5" />
              </Button>
            </Link>
            
            <div className="w-px h-6 bg-white/10 mx-1" />
            
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} data-testid={`nav-${link.label.toLowerCase()}`}>
                <Button
                  variant="ghost"
                  className={`px-4 py-2 ${
                    isActive(link.path)
                      ? 'bg-white/10 text-white'
                      : 'text-zinc-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.icon}
                  <span className="ml-2">{link.label}</span>
                </Button>
              </Link>
            ))}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            {/* Messages Button */}
            {user && openDrawer && (
              <button
                onClick={openDrawer}
                className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="nav-messages-btn"
                title="Messages"
              >
                <MessageCircle className="w-5 h-5 text-zinc-400 hover:text-cyan-400 transition-colors" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-cyan-500 rounded-full text-[10px] flex items-center justify-center font-bold px-1 text-white">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* Mobile Home Button */}
            <Link to="/dashboard" className="md:hidden" data-testid="nav-home-mobile">
              <Button
                variant="ghost"
                size="sm"
                className={`${
                  location.pathname === '/dashboard'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'text-zinc-400 hover:text-cyan-400'
                }`}
              >
                <Home className="w-5 h-5" />
              </Button>
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-3"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-indigo-400" />
                  </div>
                  <span className="hidden sm:block text-sm">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 glass border-white/10">
                <div className="px-3 py-2">
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-sm text-zinc-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer"
                  data-testid="profile-menu-btn"
                >
                  <User className="w-4 h-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-rose-400 focus:text-rose-300 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pt-4 pb-2 border-t border-white/10 mt-4"
          >
            <div className="flex flex-col gap-1">
              {/* Home Button in Mobile Menu */}
              <Link
                to="/dashboard"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-home"
              >
                <Button
                  variant="ghost"
                  className={`w-full justify-start ${
                    location.pathname === '/dashboard'
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : 'text-zinc-400 hover:text-cyan-400 hover:bg-cyan-500/10'
                  }`}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </Link>
              
              <div className="border-b border-white/10 my-2" />
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  <div
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
                      isActive(link.path)
                        ? 'bg-indigo-500/20 text-white'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    {link.icon}
                    <span>{link.label}</span>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
